import Select, { DefaultOptionType, SelectProps } from 'antd/lib/select';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import useDeclarationInputFocused from 'hooks/useDeclarationInputFocused';
import { FC, FocusEvent, ReactNode, UIEvent, useCallback, useMemo, useState } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { TemplateCheckboxes } from 'components/ui/composed/template/TemplateCheckbox';
import SearchCustomerButton from 'views/declarations/common/search-customer/SearchCustomerButton';
import { Col, Divider, Row, Space } from 'antd';
import useDeclarations from 'hooks/useDeclarations';
import { Validator } from 'views/declarations/uk/export/validations/validations';
import { kebabCase } from 'lodash';
import { TestProps } from 'core/utils/testTypes';
import { GvmsFormCustomFields } from '../../../../../views/declarations/uk/gvms/enums';
import EllipsisTextTooltip from '../../../../EllipsisTextTooltip';
import { useDeclarationValueSource } from 'components/ui/composed/declarations/source/DeclarationSourceContext';
import DeclarationValueSourceIcon from 'components/ui/composed/declarations/source/DeclarationValueSourceIcon';
import SourceIconPrefix from 'components/ui/composed/declarations/source/SourceIconPrefix';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import useFieldTemplateMetaData from 'components/ui/composed/template/useTemplateViewEditControls';
import useNonAmendableField from 'views/declarations/utils/useNonAmendableField';
import { useRegisterField } from 'components/ui/composed/template/CardContext';
import { getFormikProps, toPlaceholder } from 'views/declarations/utils/form-utils';
import { Option } from 'components/ui/base/select';
import {
    FormItem,
    InputDiv,
    LabelDiv,
    ViewOnlyLabel,
} from 'components/ui/composed/declarations/formInput/DeclarationInput.styles';
import { TextSmall } from 'components/ui/base/typography';
import { handleBlur } from 'components/ui/composed/declarations/utils';
import GvmsCustomFieldsDrawerButton from 'views/declarations/uk/gvms/components/GvmsCustomFieldsDrawerButton';
import MultipleButtons from 'components/ui/composed/declarations/MultipleButtons';
import CodelistMenuDrawer from 'components/ui/composed/declarations/formSelect/CodelistMenuDrawer';
import { StyledRef } from 'components/new-ui/base/ref/styles';
import { StyledLabel } from '../formInput/styles';
import IconTooltip from 'components/new-ui/base/tooltip/IconTooltip';
import { GlobalSelectStyles, StyledDeclarationSelect } from './styles';
import { ErrorDiv, ErrorLabel } from '../../formInput/styles';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';

const { OptGroup } = Select;

export interface SelectOption {
    id: string;
    value: string;
    disabled?: boolean;
}

export interface MultipleProps {
    node: ReactNode;
}

export type GroupSelectOption = Record<string, Readonly<SelectOption[]>>;

export type FormSelectProps = SelectProps &
    TestProps & {
        label?: string;
        tooltip?: string;
        icon?: ReactNode;
        refNumber?: string | string[];
        selectOptions?: Readonly<SelectOption[]> | Readonly<GroupSelectOption>;
        children?: ReactNode;
        refClicked?: (refNumber: string | number) => void;
        fieldProps: FieldInputProps<any>;
        fieldMeta?: FieldMetaProps<any>;
        fieldHelper?: FieldHelperProps<any>;
        required?: boolean;
        multiple?: MultipleProps;
        hideKeys?: boolean;
        viewOnly?: boolean;
        disabled?: boolean;
        amendment?: boolean;
        canLoadMore?: boolean;
        onLoadMore?: () => void;
        fieldEvents?: {
            onChange?: (value: any, option: DefaultOptionType | DefaultOptionType[]) => void;
            onBlur?: (e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLElement>) => void;
        };
        onPopupScroll?: (event: UIEvent<HTMLDivElement>) => void;
        onClear?: () => void;
        condensed?: boolean;
        multipleF?: {
            onAdd: () => void;
            onRemove: () => void;
            canAdd?: boolean;
            canRemove?: boolean;
        };
        onlyKeys?: boolean;
        specialName?: string; // Used for template meta handle of taxBox34Bis fields as of 13/04/23
        hidden?: boolean;
        elementLocator?: string;
    } & (
        | { hideCodelistMenu: boolean; codeValidation?: Validator[] }
        | { hideCodelistMenu?: boolean; codeValidation: Validator[] }
    );

export interface SelectSearchOption {
    children: any;
    key: string;
    props: any;
    value: string;
}

export const hideAllDropdowns = () => {
    const selectDropdowns = document.querySelectorAll('.ant-select-dropdown');
    if (selectDropdowns) selectDropdowns.forEach((dropdown: any) => (dropdown.style.visibility = 'hidden'));
};
export const showAllDropdowns = () => {
    const selectDropdowns = document.querySelectorAll('.ant-select-dropdown');
    if (selectDropdowns) selectDropdowns.forEach((dropdown: any) => (dropdown.style.visibility = 'visible'));
};

const DeclarationSelect: FC<FormSelectProps> = ({
    label,
    refNumber: refNumberProp,
    tooltip,
    selectOptions,
    fieldProps: fieldPropsProp,
    fieldMeta: fieldMetaProp,
    fieldHelper: fieldHelpersProp,
    multiple,
    refClicked,
    required,
    hideKeys,
    viewOnly: viewOnlyProp,
    disabled,
    fieldEvents,
    onClear: handleClear,
    condensed,
    multipleF,
    onlyKeys,
    specialName,
    hideCodelistMenu,
    codeValidation,
    hidden,
    testId,
    canLoadMore,
    onLoadMore,
    onPopupScroll,
    elementLocator,
    ...selectProps
}) => {
    const [codelistMenuDrawer, setCodelistMenuDrawer] = useState<boolean | undefined>(undefined);
    const { setFocused } = useDeclarationInputFocused();
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const location = useLocation();
    const inViewOnly = location.pathname.includes('view-only');
    const { template, templateFormik, form, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { declaration } = useDeclarations();

    useRegisterField({ path: fieldPropsProp?.name, required, hidden });
    const { isViewable, isEditable, isInvisible } = useFieldTemplateMetaData(fieldPropsProp?.name);
    const isNonAmendable = useNonAmendableField(fieldPropsProp?.name);

    const { fieldProps, fieldMeta, fieldHelpers } = useMemo(() => {
        if (template && templateFormik && form) {
            const f = getFormikProps(`${form}.defaults.${fieldPropsProp?.name}`, templateFormik);
            return { ...f, fieldHelpers: f.fieldHelper };
        }

        return { fieldProps: fieldPropsProp, fieldMeta: fieldMetaProp, fieldHelpers: fieldHelpersProp };
    }, [template, templateFormik, form, fieldPropsProp, fieldMetaProp, fieldHelpersProp]);

    const valueSource = useDeclarationValueSource(fieldPropsProp?.name, fieldMeta);
    const sourceIcon = useMemo(
        () => (valueSource ? <DeclarationValueSourceIcon {...valueSource} /> : undefined),
        [valueSource]
    );

    const handleRefClick = useCallback(() => {
        if (Array.isArray(refNumberProp)) return;

        if (refClicked) {
            refNumberProp ? refClicked(refNumberProp) : refClicked(label ?? '');
        }
    }, [refClicked, refNumberProp, label]);

    const handleEmptyValues = useCallback((item: SelectOption) => {
        return !!item.value ? item.value : item.id;
    }, []);

    const handleShowCodelistMenuDrawer = useCallback(() => {
        setCodelistMenuDrawer(true);

        hideAllDropdowns();
    }, []);

    const hideCodelistMenuDrawer = useCallback(() => {
        showAllDropdowns();

        setCodelistMenuDrawer(false);
    }, []);

    const onMouseClicked = (e: React.MouseEvent<Element>) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const showTooltip = useMemo(() => tooltip && tooltip.length > 0, [tooltip]);

    const viewOnly = useMemo(() => {
        if (template) return isTemplateViewOnly;
        if (outletContext?.amendment) return isViewable;
        return viewOnlyProp || !isEditable || inViewOnly || declaration?.archived;
    }, [
        template,
        isTemplateViewOnly,
        outletContext?.amendment,
        isViewable,
        viewOnlyProp,
        isEditable,
        inViewOnly,
        declaration?.archived,
    ]);

    const optionsRender = useMemo(() => {
        if (!selectOptions) {
            return null;
        }

        if (Array.isArray(selectOptions)) {
            return selectOptions?.map((item: SelectOption, i: number) =>
                onlyKeys ? (
                    <Option onClick={onMouseClicked} key={i} value={item.id} disabled={item.disabled}>
                        {item.id}
                    </Option>
                ) : !hideKeys ? (
                    <Option onClick={onMouseClicked} key={i} value={item.id} disabled={item.disabled}>
                        {`${item.id}${item.value ? ' - ' + item.value : ''}`}
                    </Option>
                ) : (
                    <Option onClick={onMouseClicked} key={i} value={item.id} disabled={item.disabled}>
                        {handleEmptyValues(item)}
                    </Option>
                )
            );
        }

        return Object.entries(selectOptions as Readonly<GroupSelectOption>).map(([group, options]) => {
            return (
                <OptGroup label={group}>
                    {options?.map((item: SelectOption, i: number) =>
                        !hideKeys ? (
                            <Option onClick={onMouseClicked} key={i} value={item.id}>
                                {`${item.id} - ${item.value}`}
                            </Option>
                        ) : (
                            <Option onClick={onMouseClicked} key={i} value={item.id}>
                                {handleEmptyValues(item)}
                            </Option>
                        )
                    )}
                </OptGroup>
            );
        });
    }, [handleEmptyValues, hideKeys, onlyKeys, selectOptions]);

    const refNumber = useMemo(() => {
        if (Array.isArray(refNumberProp)) {
            return (
                <>
                    {refNumberProp.map((ref) => (
                        <StyledRef key={`${label}-${ref}`}>{ref}</StyledRef>
                    ))}
                </>
            );
        } else if (typeof refNumberProp === 'string') {
            return <StyledRef>{refNumberProp}</StyledRef>;
        } else {
            return null;
        }
    }, [refNumberProp, label]);

    const fillButtonsDisabled = useMemo(
        () => template && !isEditable && !isViewable,
        [isEditable, isViewable, template]
    );
    const handleTemplateCheckboxChange = useMemo(() => {
        return fieldEvents?.onChange
            ? (value: any) => fieldEvents.onChange!(value, [])
            : selectProps.onChange
            ? (value: any) => {
                  fieldProps.onChange({ target: { value, name: fieldProps.name } });
                  selectProps.onChange!(value, []);
              }
            : undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldEvents?.onChange, fieldProps.onChange, selectProps.onChange]);

    const viewOnlyValues = useMemo(() => {
        const selectedCodelist = (selectOptions as any)?.find((o: { id: string }) => o.id === fieldPropsProp?.value);
        const value = selectedCodelist ? `${selectedCodelist.id} - ${selectedCodelist.value}` : '-';

        return {
            value,
            tooltipTitle: selectedCodelist?.value,
        };
    }, [fieldPropsProp?.value, selectOptions]);

    const handlePopupScroll = (event: UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget as HTMLDivElement;
        const { scrollTop, scrollHeight, clientHeight } = target;

        if (scrollTop + clientHeight + 100 >= scrollHeight && canLoadMore) {
            onLoadMore?.();
        }
    };

    const render = useMemo(
        () => (
            <>
                <GlobalSelectStyles />
                {viewOnly ? (
                    <>
                        <ViewOnlyLabel>{label && label}:</ViewOnlyLabel>
                        {template && <TemplateCheckboxes disabled fieldPath={fieldPropsProp?.name} />}
                        {sourceIcon}
                        <EllipsisTextTooltip tooltipTitle={viewOnlyValues.tooltipTitle}>
                            <TextSmall
                                data-testid={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {viewOnlyValues?.value}
                            </TextSmall>
                        </EllipsisTextTooltip>
                    </>
                ) : (
                    <Row
                        style={{
                            width: '100%',
                        }}
                        justify="space-between"
                        align="middle"
                    >
                        <Col span={10}>
                            <LabelDiv condensed={condensed}>
                                {refNumber}
                                {label && (
                                    <StyledLabel htmlFor={fieldProps?.name}>
                                        {required && <LabelRequiredSymbol />}
                                        <span>{label}</span>
                                        {showTooltip && <IconTooltip title={label} onClick={handleRefClick} />}
                                        <span>:</span>
                                    </StyledLabel>
                                )}
                            </LabelDiv>
                            {template && (
                                <TemplateCheckboxes
                                    required={required}
                                    fieldPath={fieldPropsProp?.name}
                                    specialName={specialName}
                                    onChange={handleTemplateCheckboxChange}
                                />
                            )}
                        </Col>
                        <Col span={14}>
                            <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                                <InputDiv>
                                    <div style={{ flex: '1', maxWidth: '100%', width: '100%', minWidth: '0' }}>
                                        <SourceIconPrefix icon={sourceIcon}>
                                            <StyledDeclarationSelect
                                                id={fieldProps?.name}
                                                disabled={disabled || isInvisible || isNonAmendable}
                                                placeholder={
                                                    selectProps.placeholder ?? toPlaceholder(label, 'Select') ?? ''
                                                }
                                                showSearch
                                                // Disable browser autofill triggering the select options
                                                autoComplete="one-time-code"
                                                allowClear
                                                onClear={handleClear}
                                                testId={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                                elementLocator={elementLocator}
                                                {...fieldProps}
                                                status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                                                filterOption={(input: string, option: SelectSearchOption) =>
                                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                                                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                }
                                                onChange={(value, option) => {
                                                    fieldProps?.onChange({ target: { value, name: fieldProps.name } });
                                                    selectProps.onChange?.(
                                                        { target: { value, name: fieldProps.name } },
                                                        option
                                                    );
                                                }}
                                                {...fieldEvents}
                                                onBlur={(e) => {
                                                    fieldHelpers?.setTouched(true);
                                                    handleBlur(setFocused, fieldProps, e);
                                                    selectProps?.onBlur?.(e);
                                                    fieldEvents?.onBlur?.(e);
                                                }}
                                                value={selectProps.value ?? fieldProps.value}
                                                onClick={onMouseClicked}
                                                onFocus={() => setFocused(true)}
                                                size={condensed ? 'middle' : 'large'}
                                                condensed={condensed}
                                                mode={selectProps.mode}
                                                getPopupContainer={
                                                    !!selectProps?.getPopupContainer
                                                        ? selectProps?.getPopupContainer
                                                        : (trigger) => trigger.parentElement
                                                }
                                                onPopupScroll={handlePopupScroll}
                                                dropdownRender={(codelist) => (
                                                    <>
                                                        {codelist}
                                                        {!hideCodelistMenu && (
                                                            <>
                                                                <Divider style={{ margin: '8px 0' }} />
                                                                <Space style={{ padding: '0 8px 4px' }}>
                                                                    <SearchCustomerButton
                                                                        disabled={fillButtonsDisabled}
                                                                        name="shows_err_w/o_name"
                                                                        condensed={condensed}
                                                                        title={'Codelist Menu'}
                                                                        onClick={handleShowCodelistMenuDrawer}
                                                                    />
                                                                </Space>
                                                            </>
                                                        )}
                                                        {declaration?.gvmsDeclaration &&
                                                            Object.values(GvmsFormCustomFields).some(
                                                                (customF) => customF === label
                                                            ) && (
                                                                <>
                                                                    <Divider style={{ margin: '8px 0' }} />
                                                                    <Space style={{ padding: '0 8px 4px' }}>
                                                                        <GvmsCustomFieldsDrawerButton
                                                                            label={label as GvmsFormCustomFields}
                                                                            fieldPath={fieldPropsProp?.name}
                                                                        />
                                                                    </Space>
                                                                </>
                                                            )}
                                                    </>
                                                )}
                                            >
                                                {optionsRender}
                                            </StyledDeclarationSelect>
                                        </SourceIconPrefix>
                                    </div>

                                    {multipleF && <MultipleButtons {...multipleF} />}
                                    {multiple ? multiple.node : <></>}
                                </InputDiv>
                                <ErrorDiv
                                    error={!!fieldMeta?.error}
                                    touched={!!fieldMeta?.touched}
                                    condensed={condensed}
                                >
                                    {!!fieldMeta?.error && !!fieldMeta?.touched && (
                                        <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                                    )}
                                </ErrorDiv>
                            </FormItem>
                        </Col>

                        {codelistMenuDrawer && codeValidation && (
                            <CodelistMenuDrawer
                                codelistData={selectOptions}
                                fieldLabel={label}
                                fieldName={fieldPropsProp?.name}
                                open={codelistMenuDrawer}
                                onClose={hideCodelistMenuDrawer}
                                codeValidation={codeValidation}
                            />
                        )}
                    </Row>
                )}
            </>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fieldMeta?.error,
            fieldMeta?.touched,
            fieldMeta?.value,
            fieldEvents?.onBlur,
            fieldEvents?.onChange,
            disabled,
            selectOptions,
            codelistMenuDrawer,
            viewOnly,
            fieldProps,
            multipleF?.canRemove,
            multipleF?.canAdd,
            sourceIcon,
        ]
    );

    if (hidden || (isInvisible && !template)) return null;

    return render;
};

export default DeclarationSelect;
